/**
 * Чистая (тестируемая без React/сети) логика вкладки «Целостность» (/audit).
 * Вынесена отдельно от ArchiveFindingsTab.tsx, чтобы TDD-проверить её без
 * рендера и моков query-хуков.
 */
import type { CoverageRow, FindingDto } from '@wave/shared';

/**
 * Подпись строки находки в таблице: номер заявки приоритетнее валюты —
 * оператору привычнее искать по номеру заявки, чем по коду валюты, а у
 * находок вида order-* он почти всегда есть. Если нет ни того ни другого — тире.
 */
export function findingRowLabel(finding: Pick<FindingDto, 'orderRef' | 'ccy'>): string {
  if (finding.orderRef) return `#${finding.orderRef}`;
  if (finding.ccy) return finding.ccy;
  return '—';
}

/**
 * Процент покрытия журналом (matched/journalRows) для одной строки `CoverageRow`.
 * 0 строк журнала — это «нет данных за сутки», а не «0% покрытия»: чат мог
 * просто молчать (выходной, отсутствие операций) — округлять его до 0% и красить
 * красным было бы ложной тревогой.
 */
export function coveragePercent(row: Pick<CoverageRow, 'journalRows' | 'matched'>): number | null {
  if (row.journalRows === 0) return null;
  return Math.round((row.matched / row.journalRows) * 100);
}

/**
 * Окно карточки покрытия — те же 7 суток, что окно проверок (спека §4.4).
 * Без окна `/archive/coverage` отдаёт всю таблицу за ретеншн (365 суток,
 * чат × сутки × валюта): и трафик десятков тысяч строк на каждое открытие
 * вкладки, и мёртвая метрика — годовое среднее не дрогнет от сегодняшнего
 * провала покрытия.
 */
export const COVERAGE_WINDOW_DAYS = 7;

const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * `[from, to]` в формате `YYYY-MM-DD` (локальные сутки оператора, как их
 * понимает `IsoDate` на сервере). Границы включительные: `days = 7` — это
 * сегодня и шесть предыдущих суток.
 */
export function coverageWindow(now: Date, days: number = COVERAGE_WINDOW_DAYS): { from: string; to: string } {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(now) };
}
